import { Slot } from '@radix-ui/react-slot'
import type { ComponentProps } from 'react'
import { cva, type VariantProps } from 'class-variance-authority'

import { cn } from '@/lib/utils'

const buttonVariants = cva(
  "inline-flex shrink-0 cursor-pointer items-center justify-center gap-2 rounded-full border border-transparent text-sm font-medium whitespace-nowrap outline-none transition-[background-color,border-color,box-shadow,color,transform] duration-150 ease-out enabled:active:scale-[0.98] disabled:cursor-not-allowed disabled:border-transparent disabled:bg-action-disabled disabled:bg-[image:none] disabled:text-action-disabled-foreground disabled:hover:bg-action-disabled disabled:hover:bg-[image:none] disabled:hover:text-action-disabled-foreground disabled:active:bg-action-disabled disabled:active:bg-[image:none] disabled:active:text-action-disabled-foreground [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring aria-invalid:border-destructive aria-invalid:outline-destructive",
  {
    variants: {
      variant: {
        default:
          'border-0! bg-action text-action-foreground hover:bg-action-hover active:bg-action-pressed',
        secondary:
          'border-control-border bg-background text-secondary-foreground hover:border-selection-border hover:bg-accentSecondary active:bg-accentSecondary/80 dark:bg-card',
        selection:
          'border-selection-border bg-accentSecondary text-accentSecondary-foreground hover:bg-accentSecondary/80 active:bg-accentSecondary',
        selected:
          'border-0! bg-action text-action-foreground hover:bg-action-hover active:bg-action-pressed',
        success:
          'bg-success text-success-foreground hover:bg-success-hover active:bg-success-pressed',
        warning:
          'border-status-warning-border bg-status-warning-background text-status-warning-foreground hover:border-status-warning-foreground hover:bg-status-warning-background active:bg-status-warning-border',
        destructive:
          'bg-status-destructive-background text-status-destructive-foreground hover:bg-destructive hover:text-destructive-foreground active:bg-destructive-pressed focus-visible:outline-destructive',
        destructiveSoft:
          'border-status-destructive-border bg-status-destructive-background text-status-destructive-foreground hover:bg-status-destructive-background hover:brightness-95 active:bg-status-destructive-border focus-visible:outline-destructive',
        outline:
          'border-control-border bg-background text-foreground hover:border-selection-border hover:bg-accentSecondary hover:text-accentSecondary-foreground active:bg-accentSecondary/80 dark:bg-card',
        ghost:
          'text-foreground hover:bg-muted hover:text-foreground active:bg-accentSecondary data-[state=open]:bg-accentSecondary',
        ghostMuted:
          'text-muted-foreground hover:bg-muted hover:text-foreground active:bg-accentSecondary data-[state=open]:bg-muted data-[state=open]:text-foreground',
        ghostOnSurface:
          'text-inherit hover:bg-background/70 hover:text-inherit active:bg-background/85 data-[state=open]:bg-background/70',
        ghostDestructive:
          'text-status-destructive-foreground hover:bg-status-destructive-background hover:text-status-destructive-foreground active:bg-status-destructive-border focus-visible:outline-destructive',
        // Links change color and underline together, with no fade, and brighten on dark surfaces.
        link: 'border-0 text-interaction underline decoration-1 underline-offset-4 transition-none hover:text-action-pressed hover:decoration-2 dark:hover:text-foreground',
        linkMuted:
          'border-0 text-muted-foreground underline decoration-1 underline-offset-4 transition-none hover:text-foreground hover:decoration-2',
        accent:
          'bg-accentSecondary text-accentSecondary-foreground hover:bg-accentSecondary/80 active:bg-accentSecondary',
        outlineCard:
          'border-control-border bg-card text-foreground hover:border-selection-border hover:bg-accentSecondary hover:text-accentSecondary-foreground active:bg-accentSecondary/80',
        outlineDestructive:
          'border-status-destructive-border bg-background text-status-destructive-foreground hover:bg-status-destructive-background hover:text-status-destructive-foreground active:bg-status-destructive-border dark:bg-card',
        outlineSuccess:
          'border-control-border bg-background text-status-success-foreground hover:border-status-success-border hover:bg-status-success-background active:bg-status-success-border dark:bg-card',
        outlineWarning:
          'border-control-border bg-background text-status-warning-foreground hover:border-status-warning-border hover:bg-status-warning-background active:bg-status-warning-border dark:bg-card',
        fulfillmentPickup:
          'border-selection-border/55 bg-accentSecondary/70 text-accentSecondary-foreground hover:border-selection-border hover:bg-accentSecondary active:bg-accentSecondary/80 dark:border-selection-border/60 dark:bg-accentSecondary/70 dark:hover:bg-accentSecondary',
        fulfillmentDelivery:
          'border-brand-periwinkle/60 bg-accent/85 text-accent-foreground hover:border-brand-periwinkle hover:bg-accent active:bg-accent/80 dark:border-brand-periwinkle/45 dark:bg-accent/80 dark:hover:bg-accent',
        soft: 'border-border/70 bg-muted text-foreground hover:border-selection-border hover:bg-accentSecondary active:bg-accentSecondary/80',
        onDark:
          'border-transparent bg-transparent text-white hover:bg-white/15 hover:text-white active:bg-white/20 focus-visible:outline-white',
        notice:
          'border-current/25 bg-card text-current hover:bg-card/80 active:bg-card disabled:border-transparent',
      },
      size: {
        default: 'h-10 px-5 has-[>svg]:pr-5 has-[>svg]:pl-4',
        header: 'h-10 px-4 has-[>svg]:pr-4 has-[>svg]:pl-3',
        xs: 'h-8 gap-1 px-3 has-[>svg]:pr-3 has-[>svg]:pl-2.5',
        compact: 'h-8 gap-1 px-2.5 text-xs has-[>svg]:pr-2.5 has-[>svg]:pl-2',
        micro: 'h-7 gap-1 px-2 text-xs has-[>svg]:pr-2 has-[>svg]:pl-1.5',
        mini: 'h-6 gap-1 px-2 text-xs has-[>svg]:pr-2 has-[>svg]:pl-1.5',
        rowCompact: 'h-7 gap-1.5 px-2 text-sm has-[>svg]:pr-2 has-[>svg]:pl-2',
        sm: 'h-9 gap-1.5 px-4 has-[>svg]:pr-4 has-[>svg]:pl-3',
        lg: 'h-12 px-7 text-base has-[>svg]:pr-7 has-[>svg]:pl-5',
        icon: 'size-10 p-0 enabled:active:scale-[0.94]',
        'icon-sm': 'size-9 p-0 enabled:active:scale-[0.94]',
        'icon-xs': 'size-8 p-0 enabled:active:scale-[0.94]',
        'icon-2xs': 'size-7 p-0 enabled:active:scale-[0.94]',
        'icon-3xs': 'size-6 p-0 enabled:active:scale-[0.94]',
        inline: 'h-auto p-0',
        'inline-xs': 'h-auto p-0 text-xs',
      },
      elevation: {
        default: 'shadow-none',
        floating: 'shadow-md hover:shadow-lg dark:shadow-none dark:hover:shadow-none',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
      elevation: 'default',
    },
  }
)

export type ButtonVariant = NonNullable<VariantProps<typeof buttonVariants>['variant']>

export type ButtonProps = ComponentProps<'button'> &
  VariantProps<typeof buttonVariants> & {
    asChild?: boolean
  }

function Button({
  className = '',
  variant = 'default',
  size = 'default',
  elevation = 'default',
  asChild = false,
  ...props
}: ButtonProps) {
  const Comp = asChild ? Slot : 'button'

  return (
    <Comp
      data-slot="button"
      data-elevation={elevation}
      data-size={size}
      data-variant={variant}
      type="button"
      className={cn(buttonVariants({ variant, size, elevation, className }))}
      {...props}
    />
  )
}

export { Button, buttonVariants }
